import type {
  WorkflowDraftDefinition,
  WorkflowEditView,
  WorkflowStepDef,
  WorkflowView,
} from '@/lib/api/generated/model';

import { rehomeParkedSteps } from './draftOps';
import { pinnedOnLoad } from './identifiers';
import { autoLayout } from './layout';
import type {
  DraftPhase,
  DraftStep,
  DraftStepType,
  DraftTransition,
  NodeId,
  Point,
  WorkflowDraft,
} from './types';

/**
 * `WorkflowView` → `WorkflowDraft`.
 *
 * Mostly a rename of snake_case to camelCase, with three decisions worth
 * naming: how the layout envelope is unwrapped, which identifiers arrive
 * pinned, and what happens to the system steps.
 */
export function fromWorkflowView(view: WorkflowView): WorkflowDraft {
  const status = view.status;

  const phases: DraftPhase[] = view.phases.map((phase) => ({
    id: phase.id,
    idPinned: pinnedOnLoad(phase.name, phase.id, status),
    name: phase.name,
    steps: (phase.steps ?? [])
      // The system steps are synthesized on every write and rejected on
      // submission, so they must not enter the draft as editable steps. The
      // contract says they are never phase members; this filter is here because
      // the read-only mapper needed the same guard and for the same reason —
      // the renderer's job is to survive whatever was persisted, not to assume.
      .filter((step) => step.type !== 'backlog' && step.type !== 'done')
      .map((step) => toDraftStep(step, status)),
  }));

  const loaded: WorkflowDraft = {
    loaded: view,
    name: view.name,
    description: view.description ?? null,
    status,
    version: view.version,
    phases,
    start: view.start ?? null,
    // The workflow-wide override, echoed back by label (spec 082/091). Was
    // previously always `null` here — `WorkflowView` had no `image` field, so
    // a saved override was lost on reload — until the backend added it and
    // started hydrating it from `execution_image_id` (mirroring `llm`).
    image: view.image ?? null,
    layout: seedLayout(view, phases),
    // A freshly loaded draft is clean. This is what keeps the navigation guard
    // silent for a workflow the user has only just created and not yet touched.
    dirty: false,
    pending: null,
  };

  // Drafts written before the holding pen was removed can still have steps
  // parked in `$unassigned`. Re-homing them here means the builder never
  // renders a band the author cannot act on — see `rehomeParkedSteps`.
  //
  // Marked dirty only when something actually moved, so an ordinary load still
  // opens clean and the navigation guard stays silent. Identity comparison is
  // enough: the change returns the same object when there is nothing to do.
  const rehomed = rehomeParkedSteps()(loaded);
  return rehomed === loaded ? loaded : { ...rehomed, dirty: true };
}

/**
 * `WorkflowEditView` → `WorkflowDraft`: a published workflow opened on its
 * pending changes when it has them (spec 144 FR-005), on itself when not.
 *
 * The pending definition is laid over the published view rather than mapped
 * on its own, so everything the definition does not carry — id, key, status,
 * the version a first save quotes — still comes from the workflow, and the one
 * mapper above decides everything else.
 */
export function fromEditView(edit: WorkflowEditView): WorkflowDraft {
  const pending = edit.pending_changes;
  if (!pending) return fromWorkflowView(edit.workflow);

  return {
    ...fromWorkflowView(withDefinition(edit.workflow, pending.definition)),
    pending: {
      revision: pending.revision,
      savedBy: pending.saved_by ?? null,
      savedVia: pending.saved_via ?? null,
      savedAt: pending.saved_at,
      outdated: pending.outdated,
    },
  };
}

/**
 * A workflow view showing `definition` in place of its own.
 *
 * Field by field, as `toRequest` is: the definition's optional fields are
 * absent rather than empty when unset, and a spread would carry that absence
 * over the workflow's own value.
 */
export function withDefinition(
  view: WorkflowView,
  definition: WorkflowDraftDefinition,
): WorkflowView {
  return {
    ...view,
    name: definition.name,
    description: definition.description ?? null,
    inputs: definition.inputs ?? [],
    phases: definition.phases ?? [],
    start: definition.start ?? null,
    image: definition.image ?? null,
    layout: definition.layout ?? null,
  };
}

function toDraftStep(
  step: WorkflowStepDef,
  status: WorkflowView['status'],
): DraftStep {
  // `type` is the API's five-member union; the two system members are filtered
  // out above, leaving three declarable types that must each be matched
  // explicitly — falling through to `ai_step` for anything not a human gate
  // would silently drop a `linked_workflow` step's `workflow` reference.
  const type: DraftStepType =
    step.type === 'human_gate'
      ? 'human_gate'
      : step.type === 'linked_workflow'
        ? 'linked_workflow'
        : 'ai_step';

  return {
    id: step.id,
    idPinned: pinnedOnLoad(step.name, step.id, status),
    name: step.name,
    type,
    prompt: step.prompt ?? '',
    llm: step.llm ?? null,
    // Meaningful only for an AI step (spec FR-004); dropped for a human gate
    // the same way `mode`/`assigneeUserId` are, just below.
    image: type === 'ai_step' ? (step.image ?? null) : null,
    // A mode on an AI step is meaningless and the service refuses it, so it is
    // dropped here rather than carried and stripped at save time.
    mode: type === 'human_gate' ? (step.mode ?? null) : null,
    assigneeUserId:
      type === 'human_gate' ? (step.assignee_user_id ?? null) : null,
    assignee: type === 'human_gate' ? (step.assignee ?? null) : null,
    workflow: type === 'linked_workflow' ? (step.workflow ?? null) : null,
    startChildren:
      type === 'linked_workflow' ? (step.start_children ?? null) : null,
    schedulingInstructions:
      type === 'linked_workflow'
        ? (step.scheduling_instructions ?? null)
        : null,
    transitions: (step.transitions ?? []).map(toDraftTransition),
  };
}

function toDraftTransition(transition: {
  key: string;
  to: string;
  when: string;
  label?: string | null;
  traversal_limit?: number | null;
}): DraftTransition {
  return {
    key: transition.key,
    to: transition.to,
    when: transition.when,
    label: transition.label ?? null,
    traversalLimit: transition.traversal_limit ?? null,
  };
}

/**
 * The arrangement to start from: what was stored, filled in where it is not.
 *
 * An imported workflow has no arrangement at all — `layout` is not in the YAML
 * shape — so one is computed on first open (FR-014). It is *not* persisted by
 * doing this: the draft is still clean, and nothing reaches the service until
 * the user saves something.
 *
 * Seeding the draft rather than computing at render time is what makes the save
 * honest. Leaving `layout` empty would mean the canvas showed a computed
 * picture while the request carried nothing, so the first save would store an
 * arrangement for whichever nodes happened to have been dragged and leave the
 * rest to be recomputed — against a graph that has since changed. What the user
 * is looking at is what gets written.
 *
 * A partial stored arrangement is completed the same way, which is the case
 * where one client added a step and another saved without it.
 */
function seedLayout(
  view: WorkflowView,
  phases: readonly DraftPhase[],
): Record<NodeId, Point> {
  const stored = unwrapLayout(view);
  const missing = phases.flatMap((phase) =>
    phase.steps.filter((step) => !(step.id in stored)),
  );
  if (missing.length === 0) return stored;

  const computed = autoLayout({
    phases: phases.map((phase) => ({
      id: phase.id,
      steps: phase.steps.map((step) => ({
        id: step.id,
        transitions: step.transitions.map((transition) => ({
          to: transition.to,
        })),
      })),
    })),
    start: view.start,
  });

  const seeded = { ...stored };
  for (const step of missing) seeded[step.id] = computed[step.id];
  return seeded;
}

/**
 * The service's `{ nodes: { [id]: {x, y} } }` envelope, flattened.
 *
 * The envelope exists so the stored arrangement can grow siblings (edge
 * waypoints, a viewport) without a migration. The draft does not need that
 * future-proofing internally, and carrying it would mean writing
 * `draft.layout.nodes[id]` at every call site — so it is unwrapped here and
 * re-wrapped in `toRequest`.
 *
 * Absent for every imported workflow: `layout` is not in the YAML shape at all.
 * That is not an error state, it is the normal one — `autoLayout` draws those.
 */
function unwrapLayout(view: WorkflowView): Record<NodeId, Point> {
  const nodes = view.layout?.nodes;
  if (!nodes) return {};

  const layout: Record<NodeId, Point> = {};
  for (const [id, point] of Object.entries(nodes)) {
    if (point) layout[id] = { x: point.x, y: point.y };
  }
  return layout;
}
